import { Theme, Tooltip } from '@mui/material';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDocumentsState,
  getHasInternalAnnotations,
  getInspectionSettings,
  getIsOCR,
  getIsSingleFile,
  getSourceLiveText,
  getTargetLiveText,
  getTextOneToOneSelected,
  getTextProcessStatus,
  inspection,
} from 'store';
import { makeStyles } from 'tss-react/mui';
import FilterList from '../shared/FilterList';
import { QRDModeToggle } from './QRDModeToggle';
import { useNoLiveTextAlert } from './NoLiveTextAlert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DocumentStates } from 'types';
import { MatchingMode } from '@verify/shared-utils';
import { TextSetting } from './TextSetting';
import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styles from './TextSettings.module.scss';

const useStyles = makeStyles()((theme: Theme) => ({
  textCheckboxLayout: {
    '&>.MuiFormControl-root > .MuiFormControlLabel-root': {
      whiteSpace: 'nowrap',
      marginRight: theme.spacing(1),
      flexShrink: 0,
    },
  },
}));

const TextSettings = () => {
  const { classes } = useStyles();
  const documentProcessed = useSelector(getTextProcessStatus);
  const targetLiveText = useSelector(getTargetLiveText);
  const sourceLiveText = useSelector(getSourceLiveText);
  const dispatch = useDispatch();
  const inspectionSettings = useSelector(getInspectionSettings);
  const textOneToOneSelected = useSelector(getTextOneToOneSelected);
  const notLiveText = !targetLiveText || !sourceLiveText;
  const documentsState = useSelector(getDocumentsState);
  const isSingleFile = useSelector(getIsSingleFile);
  const hasInternalAnnotations = useSelector(getHasInternalAnnotations);
  const bothDocumentsLoaded =
    (!isSingleFile &&
      documentsState.source === DocumentStates.LOADED &&
      documentsState.target === DocumentStates.LOADED) ||
    (isSingleFile && documentsState.target === DocumentStates.LOADED);
  const { checkResultsAgainstAnnotations, extractEmbeddedImages } = inspectionSettings;
  const isOCR = useSelector(getIsOCR);
  const fileIsOCR = isOCR.sourceIsOCR || isOCR.targetIsOCR;

  const featureFlags = useFlags();
  const showDetectEmbeddedImages = featureFlags.showDetectEmbeddedImages;
  const showCheckAgainstAnnotations = featureFlags.showCheckAgainstAnnotations;
  const showMatchQrd = featureFlags.showMatchQrd;

  const embeddedImagesToolTipText =
    'Inspect leaflets with both text and embedded images simultaneously. Images are automatically detected and matched instead of requiring a separate graphics inspection.';
  const ocrDisabledTooltipText =
    'Not available for OCRed documents. Please upload documents that both contain live text.';
  const disabledGraphicsTooltipText = 'Not available when Graphics Pixel Compare is enabled.';
  const annotationsTooltipText =
    'Include Adobe annotations and Verify Compare comments in the inspection to confirm intended changes.';
  // Pages-in-same-order UI is temporarily hidden (commented TextSetting below). Keep helpers for re-enable.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- UI commented out until product reviews results next cycle
  const pagesInSameOrderTooltipText =
    'Verify Compare can process your inspection faster by matching like pages together. ONLY select this option if the sort order is exactly the same — otherwise the inspection will fail.';

  // Automatically uncheck embedded images when a file becomes OCR
  useEffect(() => {
    if (fileIsOCR && extractEmbeddedImages) {
      dispatch(
        inspection.actions.setInspectionSettings({
          extractEmbeddedImages: false,
        }),
      );
    }
  }, [fileIsOCR, dispatch, extractEmbeddedImages]);

  const handleToggleCheckResultsAgainstAnnotations = () => {
    dispatch(
      inspection.actions.setInspectionSettings({ checkResultsAgainstAnnotations: !checkResultsAgainstAnnotations }),
    );
  };

  const handleToggleExtractEmbeddedImages = () => {
    dispatch(
      inspection.actions.setInspectionSettings({
        extractEmbeddedImages: !extractEmbeddedImages,
        fullPageGraphics: false,
      }),
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- UI commented out until product reviews results next cycle
  const handleToggleTextMatchingMode = () => {
    dispatch(
      inspection.actions.setInspectionSettings({
        textMatchingMode: textOneToOneSelected ? MatchingMode.OneToMany : MatchingMode.OneToOne,
      }),
    );
  };

  const handleToggleTextCheck = () => {
    if (inspectionSettings.text) {
      dispatch(
        inspection.actions.setInspectionSettings({
          text: false,
          extractEmbeddedImages: false,
          checkResultsAgainstAnnotations: false,
          qrdMode: false,
          textMatchingMode: undefined,
        }),
      );
    } else {
      dispatch(
        inspection.actions.setInspectionSettings({
          text: true,
          textMatchingMode: undefined,
        }),
      );
    }
  };
  const isTextCompareDisabled = !documentProcessed || notLiveText;
  const alertText = useNoLiveTextAlert({ disabled: isTextCompareDisabled });

  return (
    <div className={styles.root}>
      <div className={`${styles.checkbox} ${classes.textCheckboxLayout}`}>
        <GVCheckbox
          checked={!!inspectionSettings.text}
          onChange={handleToggleTextCheck}
          label="Text Compare"
          data-testid="text_enable"
          disabled={isTextCompareDisabled}
        />
        {isTextCompareDisabled && bothDocumentsLoaded && (
          <Tooltip title={alertText} arrow>
            <InfoOutlinedIcon className={styles.icon} data-testid="text_compare_info_icon" />
          </Tooltip>
        )}
      </div>
      {inspectionSettings.text && (
        <>
          <FilterList variant="prep" />
          <div className={styles.menu} data-testid="text_options_box">
            <div className={styles.title}>Text Options</div>
            {showDetectEmbeddedImages && (
              <TextSetting
                label="Detect embedded images"
                data-testid="detect_embedded_images"
                checked={extractEmbeddedImages || false}
                onChange={handleToggleExtractEmbeddedImages}
                disabled={inspectionSettings.fullPageGraphics || fileIsOCR}
                tooltipText={embeddedImagesToolTipText}
                disabledTooltipText={fileIsOCR ? ocrDisabledTooltipText : disabledGraphicsTooltipText}
                showAiIcon
              />
            )}
            {showCheckAgainstAnnotations && (
              <TextSetting
                label="Check against annotations"
                data-testid="check_against_annotations"
                checked={checkResultsAgainstAnnotations || false}
                disabled={!hasInternalAnnotations}
                onChange={handleToggleCheckResultsAgainstAnnotations}
                tooltipText={annotationsTooltipText}
                showAiIcon
              />
            )}
            {showMatchQrd && <QRDModeToggle />}
            {/* TODO: Re-enable pages-in-same-order UI after product reviews results next cycle.
                Also undo the OneToMany force in ctf.service.ts and inspection.service.ts,
                and uncomment the related tests in ctf.spec.ts and text-tools-prep.spec.ts.
                Until then, text matchingMode sent to CTF must remain OneToMany (off). */}
            {/* <TextSetting
              label="Pages in same order for faster inspection"
              data-testid="text_pages_in_same_order"
              checked={textOneToOneSelected}
              onChange={handleToggleTextMatchingMode}
              tooltipText={pagesInSameOrderTooltipText}
            /> */}
          </div>
        </>
      )}
    </div>
  );
};
export default TextSettings;
